<template>
  <div class="mobile-page">
    <div class="mobile-header">
      <h2><el-icon><DataLine /></el-icon> 移动端安全巡检平台</h2>
      <p>{{ greeting }}，{{ user }} ｜ 电脑端后台 · 实时同源</p>
    </div>

    <div class="mobile-body">
      <!-- 巡检统计 -->
      <template v-if="overview">
        <div class="stat-grid">
          <div class="stat-card" @click="go('/records-inspect')">
            <div class="stat-num" style="color:#1a73e8">{{ overview.totalRec }}</div>
            <div class="stat-label">巡检记录总数</div>
          </div>
          <div class="stat-card" @click="go('/records-inspect')">
            <div class="stat-num" style="color:#188038">{{ overview.todayRec }}</div>
            <div class="stat-label">今日巡检</div>
          </div>
        </div>
        <div class="mini-stats">
          <div class="mini-stat" @click="go('/hazards?mode=closure')">
            <div class="mini-num" style="color:#8430ce">{{ overview.hazardTotal }}</div>
            <div class="mini-label">隐患总数</div>
          </div>
          <div class="mini-stat" @click="go('/hazards?mode=closure&status=open')">
            <div class="mini-num" style="color:#b06000">{{ overview.hazardOpen }}</div>
            <div class="mini-label">未关闭</div>
          </div>
          <div class="mini-stat" @click="go('/hazards?mode=closure&status=closed')">
            <div class="mini-num" style="color:#188038">{{ overview.hazardClosed }}</div>
            <div class="mini-label">已关闭</div>
          </div>
        </div>
        <!-- 隐患数据降级提示（边缘节点临时故障时） -->
        <div v-if="overview.hazardsDegraded" class="degrade-tip">
          <el-icon :size="14"><WarningFilled /></el-icon>
          <span>隐患数据暂未加载完成，稍后下拉刷新或重新进入可查看</span>
        </div>
      </template>
      <div v-else-if="ovLoading" class="center-box">
        <el-icon class="is-loading" :size="26"><Loading /></el-icon>
        <p class="sub-text">统计加载中…</p>
      </div>
      <div v-else-if="ovError" class="white-card center-box" style="padding:20px">
        <p class="sub-text" style="color:#c5221f">{{ ovError }}</p>
        <el-button size="small" type="primary" plain style="margin-top:10px" @click="loadOverview">重试</el-button>
      </div>

      <!-- 功能模块 -->
      <div class="section-title"><span>功能模块</span><span class="link" @click="go('/me')">账号与设置 ›</span></div>
      <div class="module-grid">
        <div class="module-item" v-for="m in modules" :key="m.to" @click="go(m.to)">
          <span class="mi-circle" :style="{ background: m.bg, color: m.color }">
            <el-icon :size="22"><component :is="m.icon" /></el-icon>
          </span>
          <span class="mi-name">{{ m.name }}</span>
          <span class="mi-desc">{{ m.desc }}</span>
        </div>
      </div>

      <!-- 类型分布 -->
      <div v-if="overview && overview.byType && overview.byType.length" class="white-card">
        <div class="wc-title">巡检类型分布</div>
        <div class="bar-list">
          <div class="bar-item" v-for="x in overview.byType" :key="x.t">
            <span class="bar-label">{{ x.t }}</span>
            <span class="bar-track"><span class="bar-fill" :style="{ width: barW(x.c, maxType) }"></span></span>
            <span class="bar-val">{{ x.c }}</span>
          </div>
        </div>
      </div>

      <!-- 近14日趋势 -->
      <div v-if="overview && overview.recent && overview.recent.length" class="white-card">
        <div class="wc-title">近14日巡检趋势</div>
        <div class="trend-row">
          <div class="trend-day" v-for="x in overview.recent" :key="x.d" :title="x.d + ' 巡检 ' + x.c + ' 次'">
            <span class="trend-bar" :style="{ height: barH(x.c, maxRecent) }"></span>
            <span class="trend-label">{{ x.d.slice(3) }}</span>
            <span class="trend-val">{{ x.c }}</span>
          </div>
        </div>
      </div>

      <!-- 上报信息（信息收集后台） -->
      <div v-if="gatherEnabled" class="gather-entry" @click="go('/gather')">
        <el-icon :size="26" color="#1a73e8"><Upload /></el-icon>
        <div class="ge-text">
          <div class="ge-title">上报信息（实时隐患上报）</div>
          <div class="ge-sub">查看一线上报的照片/视频与处理状态 ›</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { fetchOverview, getUserName, gatherEnabled } from '../api'

const router = useRouter()
function go(path) { router.push(path) }

const overview = ref(null)
const ovLoading = ref(true)
const ovError = ref('')

// 总览本地缓存：打开立即显示上次数据，后台静默刷新（规避边缘节点慢导致的白屏等待）
const CACHE_KEY = 'overview-cache-v1'
function loadCache() {
  try { const s = localStorage.getItem(CACHE_KEY); return s ? JSON.parse(s) : null } catch { return null }
}
function saveCache(d) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data: d, ts: Date.now() })) } catch { /* 忽略 */ }
}

const user = getUserName()
const greeting = computed(() => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 9) return '早上好'
  if (h < 12) return '上午好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
})

const maxType = computed(() => Math.max(1, ...(overview.value?.byType || []).map(x => x.c)))
const maxRecent = computed(() => Math.max(1, ...(overview.value?.recent || []).map(x => x.c)))
function barW(c, max) { return Math.round((c / max) * 100) + '%' }
function barH(c, max) { return Math.max(4, Math.round((c / max) * 56)) + 'px' }

const allModules = [
  { name: '巡检计划', desc: '日常/特别/长假', to: '/plans', icon: 'Calendar', bg: '#e8f0fe', color: '#1a73e8' },
  { name: '巡检安排', desc: '人员班次值班', to: '/duty', icon: 'Timer', bg: '#fef7e0', color: '#b06000' },
  { name: '值班领导', desc: '节假日总负责', to: '/duty-leaders', icon: 'UserFilled', bg: '#f3e8fd', color: '#8430ce' },
  { name: '异常报告', desc: '现场异常上报', to: '/hazards?mode=reports', icon: 'Warning', bg: '#fce8e6', color: '#c5221f' },
  { name: '巡检记录', desc: '逐项检查留痕', to: '/records-inspect', icon: 'Checked', bg: '#e6f4ea', color: '#188038' },
  { name: '隐患闭环', desc: '处置/复查/关闭', to: '/hazards?mode=closure', icon: 'Finished', bg: '#e0f2f1', color: '#00695c' }
]
const modules = computed(() => {
  const list = [...allModules]
  if (gatherEnabled()) list.push({ name: '上报信息', desc: '照片视频上报', to: '/gather', icon: 'Upload', bg: '#e8f0fe', color: '#0b57d0' })
  return list
})

async function loadOverview() {
  const cache = loadCache()
  if (cache && cache.data) {
    // 有缓存：立即显示旧数据，后台刷新不闪 loading
    overview.value = cache.data
    ovLoading.value = false
  } else {
    ovLoading.value = true
  }
  ovError.value = ''
  try {
    const r = await fetchOverview()
    overview.value = r.data || null
    if (r.data) saveCache(r.data)
  } catch (e) {
    if (!cache || !cache.data) ovError.value = e.message || '统计加载失败'
  } finally {
    ovLoading.value = false
  }
}

onMounted(loadOverview)
</script>

<style scoped>
.mobile-header { border-radius: 0 0 20px 20px; padding-bottom: 26px; }

.mini-stats { display: flex; gap: 8px; margin-top: 10px; }
.degrade-tip {
  display: flex; align-items: center; gap: 6px; margin-top: 10px;
  background: #fef7e0; color: #b06000; border-radius: 10px;
  padding: 8px 12px; font-size: 12px; line-height: 1.4;
}
.mini-stat {
  flex: 1; background: #fff; border-radius: 12px; padding: 10px 8px; text-align: center;
  box-shadow: 0 3px 10px rgba(31,45,61,.06); cursor: pointer;
}
.mini-stat:active { transform: scale(.97); }
.mini-num { font-size: 22px; font-weight: 800; line-height: 1.1; }
.mini-label { font-size: 11.5px; color: #909399; margin-top: 2px; }

/* 模块宫格 */
.module-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.module-item {
  background: #fff; border-radius: 12px; padding: 10px 4px; display: flex; flex-direction: column;
  align-items: center; text-align: center; box-shadow: 0 3px 10px rgba(31,45,61,.05);
  cursor: pointer; transition: transform .15s;
}
.module-item:active { transform: scale(.95); }
.mi-circle {
  width: 42px; height: 42px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
}
.mi-name { font-size: 12.5px; font-weight: 700; color: #303133; margin-top: 6px; }
.mi-desc { font-size: 10px; color: #b0b3bb; margin-top: 1px; display: none; }

.wc-title { font-size: 13.5px; font-weight: 700; color: #303133; margin-bottom: 10px; }
.bar-list { display: flex; flex-direction: column; gap: 7px; }
.bar-item { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.bar-label { width: 76px; flex-shrink: 0; color: #5f6368; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bar-track { flex: 1; height: 10px; background: #f1f3f4; border-radius: 5px; overflow: hidden; }
.bar-fill { display: block; height: 100%; background: linear-gradient(90deg, #1a73e8, #4285f4); border-radius: 5px; }
.bar-val { width: 30px; text-align: right; color: #3c4043; font-weight: 600; }

.trend-row { display: flex; align-items: flex-end; gap: 5px; height: 96px; overflow-x: auto; padding: 4px 2px; }
.trend-day { flex: 1; min-width: 26px; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 2px; }
.trend-bar { width: 70%; background: linear-gradient(180deg, #4285f4, #1a73e8); border-radius: 4px 4px 0 0; }
.trend-label { font-size: 10px; color: #9aa0a6; }
.trend-val { font-size: 10px; color: #5f6368; font-weight: 600; }

.gather-entry {
  display: flex; align-items: center; gap: 12px; background: #fff; border-radius: 14px;
  padding: 14px; box-shadow: 0 3px 12px rgba(31,45,61,.06); cursor: pointer; margin-top: 14px;
}
.gather-entry:active { transform: scale(.98); }
.ge-title { font-size: 14px; font-weight: 700; color: #303133; }
.ge-sub { font-size: 12px; color: #909399; margin-top: 2px; }
</style>
